'use client';

/**
 * DiscoverBcUseCases — concrete scenarios that go beyond "exchange
 * a card at a meeting". Pulled from Part 4 §11.2 and §14.3 examples.
 */

import React from 'react';
import { Locale } from '../../../../i18n-config';
import SectionLabel from '../home/SectionLabel';
import { useTranslation } from '@/i18n/client';
import { HiBriefcase, HiCake, HiKey, HiTruck } from 'react-icons/hi2';

type Props = { lang: Locale };

type Key = 'reception' | 'field' | 'event' | 'storefront';

const ITEMS: { key: Key; Icon: typeof HiBriefcase }[] = [
  { key: 'reception', Icon: HiKey },
  { key: 'field', Icon: HiTruck },
  { key: 'event', Icon: HiBriefcase },
  { key: 'storefront', Icon: HiCake },
];

const DiscoverBcUseCases = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  const label = t('DiscoverBc/UseCases/badge');
  const headlineA = t('DiscoverBc/UseCases/title1');
  const headlineB = t('DiscoverBc/UseCases/title2');
  const sub = t('DiscoverBc/UseCases/subtitle');

  return (
    <section
      id="use-cases"
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0A0E27',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num="03" label={label} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {headlineA} <span style={{ color: '#B4C1D8' }}>{headlineB}</span>
        </h2>
        <p
          style={{
            color: '#B4C1D8',
            fontSize: 18,
            maxWidth: 720,
            lineHeight: 1.5,
            margin: '0 0 48px',
          }}
        >
          {sub}
        </p>

        <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          {ITEMS.map(({ key, Icon }) => {
            return (
              <article
                key={key}
                className="rounded-2xl p-6"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div
                  className="inline-flex items-center justify-center rounded-lg mb-4"
                  style={{
                    width: 40,
                    height: 40,
                    background: 'rgba(167,139,250,0.12)',
                    border: '1px solid rgba(167,139,250,0.30)',
                  }}
                >
                  <Icon style={{ width: 20, height: 20, color: '#A78BFA' }} />
                </div>
                <div
                  className="mono"
                  style={{
                    fontSize: 11,
                    letterSpacing: 1.5,
                    color: '#A78BFA',
                    textTransform: 'uppercase',
                    marginBottom: 6,
                  }}
                >
                  {t(`DiscoverBc/UseCases/${key}/kicker`)}
                </div>
                <h3
                  className="font-semibold"
                  style={{
                    fontSize: 18,
                    color: '#F5F7FA',
                    margin: '0 0 8px',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {t(`DiscoverBc/UseCases/${key}/title`)}
                </h3>
                <p
                  style={{
                    color: '#B4C1D8',
                    fontSize: 14,
                    lineHeight: 1.55,
                    margin: 0,
                  }}
                >
                  {t(`DiscoverBc/UseCases/${key}/body`)}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default DiscoverBcUseCases;
